'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import logTrailDetailsVisitationCommunityHeatmapTapped from '@alltrails/amplitude/events/logTrailDetailsVisitationCommunityHeatmapTapped';
import logTrailDetailsVisitationDaySelected from '@alltrails/amplitude/events/logTrailDetailsVisitationDaySelected';
import logTrailDetailsVisitationDisplayModeChanged from '@alltrails/amplitude/events/logTrailDetailsVisitationDisplayModeChanged';
import logTrailDetailsVisitationHourSelected from '@alltrails/amplitude/events/logTrailDetailsVisitationHourSelected';
import logTrailDetailsVisitationMonthSelected from '@alltrails/amplitude/events/logTrailDetailsVisitationMonthSelected';
import logTrailDetailsVisitationViewed from '@alltrails/amplitude/events/logTrailDetailsVisitationViewed';
import type VisitationHour from '@alltrails/amplitude/enums/VisitationHour';
import useUser from '@alltrails/context/hooks/useUser';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import Typography from '@alltrails/core/components/Typography';
import Dropdown from '@alltrails/denali/components/Dropdown';
import DynamicButton from '@alltrails/denali/components/DynamicButton';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import Heading from '@alltrails/denali/components/Heading';
import HeatmapsUpsellModal from '@alltrails/heatmaps/components/HeatmapsUpsellModal';
import useHeatmapsPeakValidation from '@alltrails/heatmaps/hooks/useHeatmapsPeakValidation';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import type TrailVisitation from '@alltrails/types/TrailVisitation';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsTrailMapOpen } from '@/utils/trailPageSlice';
import cardStyles from '../CardModule/CardModule.module.scss';
import styles from './Visitation.module.scss';
import VisitationChart from './VisitationChart';

type DayToken = TrailVisitation['daily']['days'][number]['token'];
type MonthToken = TrailVisitation['monthly']['months'][number]['token'];

const TOKEN_TO_DOW: Record<DayToken, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

function sortDaysFromAnchor<T extends { token: DayToken }>(days: T[], anchor: number): T[] {
  return [...days].sort((a, b) => ((TOKEN_TO_DOW[a.token] - anchor + 7) % 7) - ((TOKEN_TO_DOW[b.token] - anchor + 7) % 7));
}
const LATE_HOUR_THRESHOLD = 21;
const REF_SUNDAY_MS = Date.UTC(2024, 0, 7);

type View = Extract<keyof TrailVisitation, 'daily' | 'monthly'>;
type HourData = TrailVisitation['daily']['days'][number]['hours'][number];

function hourLabel(token: string, intl: IntlShape) {
  const h = Number(token);
  if (isNaN(h)) return token;
  return intl.formatDate(new Date(Date.UTC(2024, 0, 1, h)), { hour: 'numeric', timeZone: 'UTC' }).toLowerCase();
}

function tokenToVisitationHour(token: string): VisitationHour {
  const h = Number(token);
  if (h < 12) return `am ${h}` as VisitationHour;
  if (h === 12) return 'pm 12';
  return `pm ${h - 12}` as VisitationHour;
}

function findClosestHourIdx(hrs: HourData[], currentHour: number) {
  let best = 0;
  let bestDiff = Infinity;
  hrs.forEach((h, i) => {
    const diff = Math.abs(Number(h.token) - currentHour);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  });
  return best;
}

const MONTH_TOKENS: MonthToken[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function filterDayHours(hrs: HourData[]) {
  return hrs.filter(h => {
    const n = Number(h.token);
    return n >= 6 && n <= 18;
  });
}

function localTime() {
  const d = new Date();
  return { dayOfWeek: d.getDay(), hour: d.getHours(), month: d.getMonth() };
}

type Props = { visitation: TrailVisitation; trailId: number };

// https://www.figma.com/design/O0IQnhTIdAc8sbNjbEKDpa/Trail-Page-Web-H1--26?node-id=458-78559&m=dev
export default function Visitation({ visitation, trailId }: Props) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const dynamicStyleIds = useAppSelector(state => state.map.dynamicStyleIds);
  const user = useUser();
  const { showHeatmapsUpsellModal, onHeatmapsUpsellModalCloseRequest, handleHeatmapFailedEntitlementCheck } = useHeatmapsPeakValidation();
  const [view, setView] = useState<View>('daily');
  const [menuOpen, setMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasLoggedViewEvent = useRef(false);
  const isVisible = useIsVisible(contentRef);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (contentRef.current) setMinHeight(contentRef.current.offsetHeight);
  }, []);

  const [today, setToday] = useState(0);
  const [selectedDayIdx, setSelectedDayIdx] = useState(0);
  const [activeHourIdx, setActiveHourIdx] = useState(0);
  const [activeMonthIdx, setActiveMonthIdx] = useState(0);

  const sortedDays = useMemo(() => sortDaysFromAnchor(visitation.daily.days, today), [visitation.daily.days, today]);
  const sortedMonths = useMemo(
    () => [...visitation.monthly.months].sort((a, b) => MONTH_TOKENS.indexOf(a.token) - MONTH_TOKENS.indexOf(b.token)),
    [visitation.monthly.months]
  );

  useEffect(() => {
    const { dayOfWeek, hour, month } = localTime();

    // After 9pm local time, lead with tomorrow since today is nearly over
    const showTomorrow = hour >= LATE_HOUR_THRESHOLD;
    const effectiveDay = showTomorrow ? (dayOfWeek + 1) % 7 : dayOfWeek;
    const effectiveHour = showTomorrow ? 0 : hour;

    const sorted = sortDaysFromAnchor(visitation.daily.days, effectiveDay);

    setToday(effectiveDay);
    setActiveHourIdx(findClosestHourIdx(filterDayHours(sorted[0].hours), effectiveHour));

    const monthIdx = sortedMonths.findIndex(m => m.token === MONTH_TOKENS[month]);
    setActiveMonthIdx(monthIdx >= 0 ? monthIdx : 0);
  }, [sortedMonths]);

  useEffect(() => {
    if (isVisible && !hasLoggedViewEvent.current) {
      const activeHour = filterDayHours(sortedDays[selectedDayIdx].hours)[activeHourIdx];
      const activeMonth = sortedMonths[activeMonthIdx];
      if (activeHour && activeMonth) {
        logTrailDetailsVisitationViewed({
          trail_id: trailId,
          default_display_mode: view === 'daily' ? 'by day' : 'by month',
          default_day_selected: sortedDays[selectedDayIdx].token,
          default_hour_selected: tokenToVisitationHour(activeHour.token),
          default_month_selected: activeMonth.token
        });
        hasLoggedViewEvent.current = true;
      }
    }
  }, [isVisible, trailId, view, sortedDays, selectedDayIdx, activeHourIdx, sortedMonths, activeMonthIdx]);

  const hours = filterDayHours(sortedDays[selectedDayIdx].hours);

  const handleDaySelect = (i: number) => {
    setSelectedDayIdx(i);
    logTrailDetailsVisitationDaySelected({ trail_id: trailId, visitation_day_selected: sortedDays[i].token });
  };

  const handleBarSelect = useCallback(
    (i: number) => {
      if (view === 'daily') {
        setActiveHourIdx(i);
      } else {
        setActiveMonthIdx(i);
      }
    },
    [view]
  );

  const handleBarInteract = useCallback(
    (i: number) => {
      if (view === 'daily') {
        const hour = filterDayHours(sortedDays[selectedDayIdx].hours)[i];
        if (hour) {
          logTrailDetailsVisitationHourSelected({ trail_id: trailId, visitation_hour_selected: tokenToVisitationHour(hour.token) });
        }
      } else {
        const month = sortedMonths[i];
        if (month) {
          logTrailDetailsVisitationMonthSelected({ trail_id: trailId, visitation_month_selected: month.token });
        }
      }
    },
    [view, trailId, sortedDays, selectedDayIdx, sortedMonths]
  );

  const dailyData = hours.map(h => ({
    name: hourLabel(h.token, intl),
    value: h.value,
    label: visitation.busynessLabels[h.busynessLabelIndex]
  }));

  const monthlyData = sortedMonths.map(m => ({
    name: intl.formatDate(new Date(Date.UTC(2024, MONTH_TOKENS.indexOf(m.token), 1)), { month: 'short', timeZone: 'UTC' }),
    value: m.value,
    label: visitation.busynessLabels[m.busynessLabelIndex]
  }));

  const xAxisTicks = useMemo(() => [hourLabel('6', intl), hourLabel('12', intl), hourLabel('18', intl)], [intl]);

  const viewLabel = view === 'daily' ? intl.formatMessage({ defaultMessage: 'By day' }) : intl.formatMessage({ defaultMessage: 'By month' });

  return (
    <div ref={contentRef} className={classNames(cardStyles.card, styles.mobileWrapper)} style={minHeight !== undefined ? { minHeight } : undefined}>
      <div className={classNames(cardStyles.inner, styles.mobileInner)}>
        <div className={styles.header}>
          <Heading as="h2" size="300" className={classNames(styles.title, { [styles.titleMonthly]: view === 'monthly' })}>
            <FormattedMessage defaultMessage="Visitation" />
          </Heading>
          <DropdownMenu
            anchor={
              <Dropdown
                className={styles.viewDropdown}
                text={viewLabel}
                variant="secondary"
                size="sm"
                width="auto"
                onClick={() => setMenuOpen(o => !o)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                aria-controls="visitation-menu"
              />
            }
            isOpen={menuOpen}
            onCloseRequest={() => setMenuOpen(false)}
            placement="bottom-end"
            options={[
              {
                title: intl.formatMessage({ defaultMessage: 'By day' }),
                selected: view === 'daily',
                onClick: () => {
                  if (view !== 'daily') {
                    logTrailDetailsVisitationDisplayModeChanged({ trail_id: trailId, display_mode_from: 'by month', display_mode_to: 'by day' });
                  }
                  setView('daily');
                  setMenuOpen(false);
                }
              },
              {
                title: intl.formatMessage({ defaultMessage: 'By month' }),
                selected: view === 'monthly',
                onClick: () => {
                  if (view !== 'monthly') {
                    logTrailDetailsVisitationDisplayModeChanged({ trail_id: trailId, display_mode_from: 'by day', display_mode_to: 'by month' });
                  }
                  setView('monthly');
                  setMenuOpen(false);
                }
              }
            ]}
          />
        </div>

        <div className={classNames(styles.body, { [styles.bodyMonthly]: view === 'monthly' })}>
          <div className={classNames(styles.dayPicker, { [styles.dayPickerHidden]: view === 'monthly' })}>
            {sortedDays.map((day, i) => (
              <button
                key={day.token}
                type="button"
                className={classNames(styles.dayBtn, { [styles.dayBtnActive]: i === selectedDayIdx })}
                onClick={() => handleDaySelect(i)}
                tabIndex={view === 'monthly' ? -1 : undefined}
              >
                <span className={styles.dayLabel}>
                  {intl.formatDate(new Date(REF_SUNDAY_MS + TOKEN_TO_DOW[day.token] * 86400000), { weekday: 'short', timeZone: 'UTC' })}
                </span>
              </button>
            ))}
          </div>

          <div className={styles.chartSection}>
            <div className={styles.chartArea}>
              <VisitationChart
                view={view}
                dailyData={dailyData}
                monthlyData={monthlyData}
                xAxisTicks={xAxisTicks}
                activeHourIdx={activeHourIdx}
                activeMonthIdx={activeMonthIdx}
                maxDailyValue={visitation.daily.range.max}
                maxMonthlyValue={visitation.monthly.range.max}
                onBarInteract={handleBarInteract}
                onBarSelect={handleBarSelect}
              />
              <Typography variant="text100" color="primary" className={styles.footer}>
                {visitation.footer}
              </Typography>
            </div>
          </div>
        </div>
        <div className={styles.actionContainer}>
          <div className={styles.buttonContainer}>
            <DynamicButton
              className={styles.communityHeatmapButton}
              imageAlt=""
              onClick={() => {
                logTrailDetailsVisitationCommunityHeatmapTapped({ trail_id: trailId });
                const applyStyles = () => {
                  if (!dynamicStyleIds.includes('newHeatmap')) {
                    dispatch(updateDynamicStyleIds([...dynamicStyleIds, 'newHeatmap']));
                  }
                };
                dispatch(setIsTrailMapOpen(true));
                if (user?.featureEntitlements?.includes('community_heat_maps')) {
                  applyStyles();
                } else {
                  handleHeatmapFailedEntitlementCheck(applyStyles);
                }
              }}
              src="https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-communityHeatmap@2x.png"
              size="md"
              text={
                <Typography variant="heading200">
                  <FormattedMessage defaultMessage="Community heatmap" />
                </Typography>
              }
            />
          </div>
        </div>
      </div>
      <HeatmapsUpsellModal
        isOpen={showHeatmapsUpsellModal}
        onCloseRequest={onHeatmapsUpsellModalCloseRequest}
        upsellTrigger="visitation community heatmap"
        trailId={trailId}
      />
    </div>
  );
}
